import { HttpErrorResponse } from "@angular/common/http";
import {
	ChangeDetectorRef,
	Directive,
	ElementRef,
	Input,
	input,
	OnChanges,
	OnDestroy,
	OnInit,
	Renderer2,
	SimpleChanges,
	TemplateRef,
	TrackByFunction,
	viewChild,
} from "@angular/core";
import { AutoCompleteComponent } from "common/autoComplete/auto-complete.component";
import { Selection } from "common/collection/selection.model";
import { AddComponent } from "common/data/add.component";
import { EditableComponent } from "common/data/editable.component";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { NotificationService } from "core/notification.service";
import { combineLatest, Observable, Subject } from "rxjs";
import { SelectionStoreService } from "common/selection/state/selection-store.service";
import { SelectionRepository } from "common/selection/state/selection.repository";
import { Selectable } from "common/selection/state/selectable.model";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { getIdKeyOfTypeId, getTypeIdOfTypeIdString } from "common/selection/helper/selection-helper";

type TypedId = stages.core.TypedId;

export enum Mode {
	VIEW = "VIEW",
	ADD = "ADD",
	RENAME = "RENAME",
	DELETE = "DELETE",
	EDIT = "EDIT",
	UPDATE = "UPDATE",
	SELECT = "SELECT",
	REARRANGE = "REARRANGE",
}

export interface Button {
	class: string;
	translate: string;

	disabled?(): boolean;

	visible?(): boolean;

	click(): void;
}

@Directive()
export abstract class DataViewComponent<T> implements OnInit, OnDestroy, OnChanges {
	private filterUpdated$ = new Subject<string>();
	destroy$ = new Subject<boolean>();
	Mode = Mode;
	_mode: Mode = Mode.VIEW;
	buttons?: Button[];
	selection: Selection<string>;
	activeFilters: Map<string, string> = new Map<string, string>();
	searchEngineAvailable: boolean = true;
	groupViewStates = new Map<string, Mode>();
	selectAllButtonPressed = false;
	triggerChangeCounter = 0;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	dataSource!: PageableDataSource<T>;

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	translateNone?: string;

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	autoCompleteTemplate?: TemplateRef<unknown>;

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	autoCompleteBrowseTemplate?: TemplateRef<unknown>;

	readonly renameTemplate = input<TemplateRef<unknown>>();

	readonly showMore = input<boolean>(false);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	idFn!: (item: T) => string;

	readonly idFnNew = input<(item: T) => TypedId>();

	readonly classesFn = input<(item: T) => string[]>();

	readonly inactiveFn = input<(item: T) => boolean>(() => false);

	readonly isPreselectedFn = input<(item: T) => boolean>(() => false);

	readonly isPartiallySelectedFn = input<(item: T) => boolean>(() => false);

	readonly disabledFn = input<(item: T, data: PagedResult<T>, mode: Mode) => boolean>(() => false);
	readonly disabledFnNew = input<(item: T) => boolean>(() => false);

	readonly actionInProgress = input<boolean>(false);

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	pageSize: number = 10;

	readonly addMaxLength = input(255);

	readonly addPattern = input<string>();

	readonly addPatternMessageKey = input<string>();

	readonly showSelectAllSwitch = input<boolean>(false);

	readonly showSelectDeselectButtons = input<boolean>(false);

	readonly storeIdentifier = input<string>("common");

	readonly parentIdentifier = input<(item: T) => string>();

	readonly explicitParentMode = input<boolean>(true);

	readonly autoComplete = viewChild(AutoCompleteComponent);

	readonly addComponent = viewChild(AddComponent);

	readonly coreTable = viewChild.required("coreTable", { read: ElementRef });

	editables: Record<string, EditableComponent<T>> = {};

	data$!: Observable<PagedResult<T> | SecuredPagedResult<T> | SecuredPagedResultAndInfo<T, unknown>>;

	renamed?: T;

	allElements: Selectable[] = [];

	hasItems: boolean = false;
	isSelectAllActivated!: boolean;

	constructor(
		protected chRef: ChangeDetectorRef,
		private notificationService: NotificationService,
		private renderer: Renderer2,
		private selectionStoreService: SelectionStoreService,
		private selectionRepository: SelectionRepository,
	) {
		this.selection = new Selection<string>(true);
	}

	initializeSelection(initiallySelectedValues: string[]): void {
		this.selection = new Selection<string>(true, initiallySelectedValues);
	}

	get selectable(): boolean {
		return (
			(this.mode === Mode.DELETE || this.mode === Mode.UPDATE || this.mode === Mode.SELECT) && !this.actionInProgress()
		);
	}

	get mode(): Mode {
		return this._mode;
	}

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input()
	set mode(mode: Mode) {
		const wasSelectionMode = this.selectable;
		if (this._mode === Mode.ADD && mode === Mode.VIEW) {
			window.setTimeout(() => {
				this._mode = mode;
			}, 500);
		} else {
			this._mode = mode;
		}

		if (wasSelectionMode && !this.selectable) {
			this.selection.clear();
		}
	}

	private updatePollingStatus(): void {
		if (this.dataSource) {
			if (this.mode === Mode.VIEW) {
				this.dataSource.startPolling(this.notificationService);
			} else {
				this.dataSource.stopPolling(this.notificationService);
			}
		}
	}

	ngOnInit(): void {
		this.data$ = this.dataSource.data;

		combineLatest([
			this.selectionRepository.isLoadingCommon$,
			this.selectionRepository.isSelectAllPressedCommon$,
			this.selectionRepository.getSelectableCountByIdentifierCommon$(this.storeIdentifier()),
			this.selectionRepository.getDeSelectedCountByIdentifierCommon$(this.storeIdentifier()),
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([isLoading, isSelectAllPressed, selectableCount, deselectedCount]) => {
				if (!isLoading) {
					this.isSelectAllActivated = isSelectAllPressed || (selectableCount > 0 && deselectedCount === 0);
				}
			});

		if (this.mode === Mode.SELECT) {
			this.data$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
				this.selectionRepository.setLoadingCommon(true);
				for (const item of value.items) {
					this.addSelectableCommon(item);
				}

				this.hasItems = value.items.length > 0;
				this.selectionRepository.setLoadingCommon(false);
			});
		}

		if (this.mode === Mode.VIEW) {
			this.dataSource.startPolling(this.notificationService);
		}

		this.filterUpdated$
			.asObservable()
			.pipe(debounceTime(300))
			.pipe(distinctUntilChanged())
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.onFilterInput();
			});
	}

	ngOnDestroy(): void {
		this.dataSource.stopPolling(this.notificationService);
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.dataSource?.currentValue || changes.mode?.currentValue) {
			this.updatePollingStatus();
		}
	}

	async add(item: unknown, groupId?: string): Promise<void> {
		try {
			if (groupId && this.dataSource.addToGroup) {
				await this.dataSource.addToGroup([item], groupId);
			}

			if (!groupId && this.dataSource.add) {
				await this.dataSource.add(item);
			}

			const autoComplete = this.autoComplete();
			if (autoComplete) {
				autoComplete.focus();
			}
			const addComponent = this.addComponent();
			if (addComponent) {
				addComponent.itemExist = false;
				addComponent.form().reset();
				addComponent.focus();
			}
		} catch (e: unknown) {
			if (e instanceof HttpErrorResponse && e.status === 422) {
				this.addComponent()!.itemExist = true;
				if (typeof item === "string") {
					this.addComponent()!.name = item;
				}
			} else {
				throw e;
			}
		}
	}

	async enterAdd(goToFirstPage: boolean = true): Promise<void> {
		if (goToFirstPage) {
			await this.dataSource.goTo(1);
		}
		this.mode = Mode.ADD;
	}

	enterAddByGroup(groupId: string): void {
		this.groupViewStates.set(groupId, Mode.ADD);
		for (const entry of this.groupViewStates) {
			if (entry[0] !== groupId) {
				this.groupViewStates.set(entry[0], Mode.VIEW);
			}
		}
	}

	enterDelete(): void {
		this.mode = Mode.DELETE;
	}

	cancelAdd(groupId?: string): void {
		if (groupId) {
			this.groupViewStates.set(groupId, Mode.VIEW);
		} else {
			this.mode = Mode.VIEW;
		}
	}

	rename(item: T): void {
		this.mode = Mode.RENAME;
		this.chRef.detectChanges();
		const id = this.idFn(item);
		const editable = this.editables[id];
		editable.editable = true;
		editable.focus();
	}

	enterRearrange(): void {
		this.mode = Mode.REARRANGE;
		this.chRef.detectChanges();
	}

	cancelRearrange(skipRedraw = false): void {
		this.mode = Mode.VIEW;
		if (!skipRedraw) {
			this.redrawTable();
		}
		this.dataSource.reloadPage();
	}

	async commitRearrange(items: T[]): Promise<void> {
		if (this.dataSource.rearrange) {
			await this.dataSource.rearrange(items);
			this.mode = Mode.VIEW;
			this.redrawTable();
		}
	}

	cancelRename(item: T, skipRedraw = false, renameGroup: boolean = false): void {
		const id = this.idFn(item);
		const editable = this.editables[id];
		editable.editable = false;
		if (this.mode === Mode.RENAME) {
			this.mode = Mode.VIEW;
		}
		if (!skipRedraw) {
			this.redrawTable();
		}
	}

	async commitRename(item: T, name: string, renameGroup: boolean = false): Promise<void> {
		const id = this.idFn(item);
		const editable = this.editables[id];
		if (this.dataSource.rename) {
			await this.dataSource.rename(item, name);
			this.mode = Mode.VIEW;
			editable.editable = false;
			this.redrawTable();
			return;
		}
		return undefined;
	}

	trackBy: TrackByFunction<T> = (_index, item) => {
		const idFnNew = this.idFnNew();
		if (idFnNew) {
			const typedId = idFnNew(item);
			return typedId.id + typedId.typeIdent;
		}
		return this.idFn(item);
	};

	addEditable(editable: EditableComponent<T>): void {
		const id = this.idFn(editable.item());
		this.editables[id] = editable;
	}

	onFilterChange(value: string): void {
		this.filterUpdated$.next(value);
	}

	isItemRenamed(item: T): boolean {
		if (this.mode !== Mode.RENAME) {
			return false;
		}

		const editable = this.editables[this.idFn(item)];
		return editable?.editable;
	}

	isRecentlyAdded(item: T): boolean {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		return (item as any).recentlyAdded;
	}

	getClasses(item: T): string[] {
		const classesFn = this.classesFn();
		const classes: string[] = classesFn ? [...classesFn(item)] : [];
		if (this.isItemRenamed(item)) {
			classes.push("add-item");
		}
		if (this.isRecentlyAdded(item)) {
			classes.push("new-item");
		}

		return classes;
	}

	selectDeselectAll(): void {
		const newIsSelectAllActivated = !this.isSelectAllActivated;

		this.selectionStoreService.setSelectAllPressed(newIsSelectAllActivated);
		if (newIsSelectAllActivated) {
			this.selectionStoreService.selectAllByIdentifierCommon(this.storeIdentifier());
		} else {
			this.selectionStoreService.deselectAllByIdentifierCommon(this.storeIdentifier());
		}
		// trigger recalculate isSelected and isPreselected in pipes
		this.triggerChangeCounter++;
	}

	selectAll(): void {
		this.selectionRepository.setSelectAllPressedCommon(true);
		this.selectionRepository.setDeselectAllPressedCommon(false);
		this.selectionStoreService.selectAllByIdentifierCommon(this.storeIdentifier());
	}

	deselectAll(): void {
		this.selectionRepository.setDeselectAllPressedCommon(true);
		this.selectionRepository.setSelectAllPressedCommon(false);
		this.selectionStoreService.deselectAllByIdentifierCommon(this.storeIdentifier());
	}

	addSelectableCommon(item: T): void {
		const idFnNew = this.idFnNew();
		if (idFnNew) {
			const typedId = idFnNew(item);
			const selectionKey = getIdKeyOfTypeId(typedId);
			const selectionTarget = this.selectionRepository.getEntityCommon(selectionKey, this.storeIdentifier());
			if (!selectionTarget) {
				const initiallySelectedChildrenCount = (item as unknown as Selectable).hasOwnProperty("initiallySelectedCount")
					? (item as unknown as Selectable).initiallySelectedCount
					: 0;

				const selectableChildrenCount = (item as unknown as Selectable).hasOwnProperty("selectableChildrenCount")
					? (item as unknown as Selectable).selectableChildrenCount
					: 0;

				const storeIdentifier = this.storeIdentifier();
				const disabledFnNew = this.disabledFnNew();
				const parentIdentifier = this.parentIdentifier();
				this.selectionStoreService.addNewSelectableCommon(
					typedId,
					this.isSelectAllActivated ? this.isSelectAllActivated : this.isPreselectedFn()(item),
					this.isPreselectedFn()(item),
					this.explicitParentMode(),
					disabledFnNew ? !disabledFnNew(item) : true,
					parentIdentifier ? getTypeIdOfTypeIdString(parentIdentifier(item)) : undefined,
					storeIdentifier ? storeIdentifier : undefined,
					this.isPartiallySelectedFn()(item),
					selectableChildrenCount ?? 0,
					initiallySelectedChildrenCount ?? undefined,
					selectableChildrenCount ?? 0,
				);
			}
		}
	}

	toggleSelection(item: T): void {
		const idFnNew = this.idFnNew();
		if (idFnNew) {
			this.selectAllButtonPressed = false;
			const typedId = idFnNew(item);
			const selectionKey = getIdKeyOfTypeId(typedId);
			const selectionTarget = this.selectionRepository.getEntityCommon(selectionKey, this.storeIdentifier());
			if (selectionTarget) {
				this.toggleExisting(selectionTarget);
			} else {
				const disabledFnNew = this.disabledFnNew();
				const parentIdentifier = this.parentIdentifier();
				this.selectionStoreService.addNewSelectableCommon(
					typedId,
					this.isPreselectedFn()(item),
					this.isPreselectedFn()(item),
					this.explicitParentMode(),
					disabledFnNew ? !disabledFnNew(item) : true,
					// TODO parentIdentifier not needed anymore!
					parentIdentifier ? getTypeIdOfTypeIdString(parentIdentifier(item)) : undefined,
					this.storeIdentifier(),
				);
			}
			this.triggerChangeCounter++;
		} else {
			this.selection.toggle(this.idFn(item));
		}
	}

	private toggleExisting(selectable: Selectable): void {
		if (selectable?.isSelected && selectable.indeterminate) {
			this.selectionStoreService.selectCommon(selectable, this.explicitParentMode());
		} else if (selectable?.isSelected && !selectable.indeterminate) {
			this.selectionStoreService.deselectCommon(selectable, this.explicitParentMode());
		} else if (!selectable?.isSelected) {
			this.selectionStoreService.selectCommon(selectable, this.explicitParentMode());
		}
	}

	async onFilterInput(): Promise<void> {
		if (this.dataSource.filter && this.hasActiveFilters()) {
			try {
				await this.dataSource.filter(this.activeFilters);
				this.searchEngineAvailable = true;
			} catch {
				this.searchEngineAvailable = false;
			}
		} else {
			this.dataSource.reloadPage();
		}
	}

	hasActiveFilters(): boolean {
		if (this.activeFilters.size === 0) {
			return false;
		}

		const nonEmptyValues = [...this.activeFilters.values()].filter((value: string) => !!value && value.length > 0);
		return nonEmptyValues.length > 0;
	}

	protected redrawTable(): void {
		// redraw table on edge, without this code the table layout is broken after rename (e.g. workspace attribute rename)
		const ua = window.navigator.userAgent;
		if (this.coreTable && ua.indexOf("Edge/") > 0) {
			this.renderer.setStyle(this.coreTable().nativeElement, "display", "block");
			this.chRef.detectChanges();
			setTimeout(() => {
				this.renderer.setStyle(this.coreTable().nativeElement, "display", "table");
			}, 200);
		}
	}
}
